import { categoryUrl } from '../../utils/slug';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { retranslatePage } from '../../utils/googleTranslate';
import {
    ArrowRight,
    BadgeCheck,
    Heart,
    HeartHandshake,
    LockKeyhole,
    Mail,
    MapPin,
    MessageCircleHeart,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import SupportChatWidget from '../Support/SupportChatWidget';
import LoginModal from '../Modals/LoginModal';
import { useSupportUnread } from '../../hooks/useSupportUnread';
import API from '../../utils/api';
import { CONTACT } from '../../utils/contactInfo';
import './Footer.css';

const GooglePlayMark = () => (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <path fill="#00c3ff" d="M3.6 1.9c-.3.3-.5.8-.5 1.4v17.4c0 .6.2 1.1.5 1.4l.1.1 9.7-9.7v-.2L3.7 1.8z" />
        <path fill="#ffce00" d="M16.6 15.3l-3.2-3.3v-.2l3.2-3.2.1.1 3.8 2.2c1.1.6 1.1 1.6 0 2.2l-3.8 2.2z" />
        <path fill="#ff3a44" d="M16.7 15.2L13.4 12 3.6 21.8c.4.4 1 .4 1.6.1l11.5-6.7" />
        <path fill="#00d36b" d="M16.7 8.8L5.2 2.2c-.6-.3-1.2-.3-1.6.1L13.4 12l3.3-3.2z" />
    </svg>
);

const AppleMark = () => (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </svg>
);

const AndroidMark = () => (
    <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
        <path fill="currentColor" d="M17.523 15.341a.998.998 0 1 1 0-1.996.998.998 0 0 1 0 1.996m-11.046 0a.998.998 0 1 1 0-1.996.998.998 0 0 1 0 1.996m11.4-6.02 1.997-3.46a.416.416 0 1 0-.72-.416l-2.022 3.503A12.4 12.4 0 0 0 12 7.82c-1.82 0-3.55.4-5.132 1.128L4.846 5.445a.416.416 0 1 0-.72.416L6.123 9.32C2.69 11.19.34 14.66 0 18.761h24c-.34-4.1-2.69-7.57-6.123-9.44" />
    </svg>
);

// A store button: a real link once the admin has saved one, otherwise a quiet "Coming soon" badge.
const StoreBadge = ({ href, small, name, icon, download }) => (href ? (
    <a className="store-badge" href={href} {...(download ? { download: 'Gettimelam-Matrimony.apk' } : { target: '_blank', rel: 'noopener noreferrer' })} aria-label={`${small} ${name}`}>
        {icon}<span><small>{small}</small><strong>{name}</strong></span>
    </a>
) : (
    <span className="store-badge is-soon" aria-label={`${name}: coming soon`}>
        {icon}<span><small>Coming soon on</small><strong>{name}</strong></span>
    </span>
));

const Footer = () => {
    const { user } = useAuth();
    const [showSupportChat, setShowSupportChat] = useState(false);
    const [showLogin, setShowLogin] = useState(false);
    const [serviceCategories, setServiceCategories] = useState([]);
    const [categoriesLoading, setCategoriesLoading] = useState(true);
    const [appLinks, setAppLinks] = useState({ googlePlay: '', appStore: '', android: { type: '', url: '' } });
    const { unread: supportUnread, clear: clearSupportUnread } = useSupportUnread();

    // store links are managed in the admin panel (App Downloads)
    // an uploaded app file is served by this site's API; a pasted link is used as it is
    const androidHref = appLinks.android?.type === 'file' ? `${API.defaults.baseURL.replace(/\/api$/, '')}${appLinks.android.url}` : (appLinks.android?.url || '');

    useEffect(() => {
        let mounted = true;
        API.get('/app-links')
            .then(res => { if (mounted) setAppLinks({ googlePlay: res.data.googlePlay || '', appStore: res.data.appStore || '', android: res.data.android || { type: '', url: '' } }); })
            .catch(() => { });
        return () => { mounted = false; };
    }, []);

    useEffect(() => {
        let mounted = true;
        API.get('/options', { params: { category: 'servicecategory', parent: '' } })
            .then(response => {
                if (!mounted) return;
                const categories = (response.data.options || [])
                    .filter(category => typeof category === 'string' && category.trim())
                    .map(category => category.trim());
                setServiceCategories([...new Set(categories)]);
            })
            .catch(() => { if (mounted) setServiceCategories([]); })
            .finally(() => { if (mounted) setCategoriesLoading(false); });

        return () => { mounted = false; };
    }, []);

    // Footer categories arrive after Google Translate has run, so ask it to translate the new links.
    useEffect(() => {
        if (categoriesLoading) return;
        const timer = setTimeout(retranslatePage, 300);
        return () => clearTimeout(timer);
    }, [categoriesLoading, serviceCategories]);

    const handleSupportClick = () => {
        if (!user) {
            setShowLogin(true);
            return;
        }
        setShowSupportChat(true);
        clearSupportUnread();
    };

    return (
        <footer className="site-footer">
            <div className="site-footer__ornament" aria-hidden="true" />
            <div className="site-footer__inner">
                <section className="site-footer__cta" aria-labelledby="footer-cta-title">
                    <div className="site-footer__cta-icon"><HeartHandshake size={27} /></div>
                    <div>
                        <span>Begin a meaningful chapter</span>
                        <h2 id="footer-cta-title">Your forever could start with one hello.</h2>
                    </div>
                    <Link to={user ? '/browse' : '/register'}>
                        {user ? 'Explore your matches' : 'Create free profile'} <ArrowRight size={17} />
                    </Link>
                </section>

                <div className="site-footer__main">
                    <div className="site-footer__brand">
                        <Link className="site-footer__logo" to="/" aria-label="Gettimelam homepage">
                            <img src="/logo-sm.webp" alt="Gettimelam Matrimony" />
                        </Link>
                        <p>Thoughtful introductions for Tamil families who value compatibility, tradition, and genuine connection.</p>
                        <div className="site-footer__trust">
                            <span><BadgeCheck size={14} /> Verified profiles</span>
                            <span><LockKeyhole size={14} /> Privacy protected</span>
                        </div>
                    </div>

                    <nav aria-label="Profile links">
                        <span className="site-footer__eyebrow">Discover</span>
                        <h3>Find your match</h3>
                        <Link to="/browse?religion=Hindu">Hindu Matrimony</Link>
                        <Link to="/browse?religion=Muslim">Muslim Matrimony</Link>
                        <Link to="/browse?religion=Christian">Christian Matrimony</Link>
                        <Link to="/browse">Browse by location</Link>
                        <Link to="/browse">Browse by profession</Link>
                    </nav>

                    <nav aria-label="Wedding service links">
                        <span className="site-footer__eyebrow">Celebrate</span>
                        <h3>Wedding services</h3>
                        {categoriesLoading ? (
                            <div className="site-footer__category-loading" aria-label="Loading service categories">
                                <span /><span /><span /><span />
                            </div>
                        ) : (
                            serviceCategories.slice(0, 5).map(category => (
                                <Link key={category} to={categoryUrl(category)}>{category}</Link>
                            ))
                        )}
                        <Link className="site-footer__highlight-link" to="/services">
                            {serviceCategories.length > 5 ? `View all ${serviceCategories.length} categories` : 'View all services'} <ArrowRight size={12} />
                        </Link>
                    </nav>

                    <div className="site-footer__connect">
                        <span className="site-footer__eyebrow">Here to help</span>
                        <h3>Speak with our team</h3>
                        <p>Questions about your profile, plans, or finding the right match? Our team is ready to help.</p>
                        <button type="button" onClick={handleSupportClick}>
                            <MessageCircleHeart size={16} /> Chat with support
                            {user && supportUnread && <span className="site-footer__unread" />}
                        </button>
                        <div className="site-footer__contact">
                            <Link to="/contact"><Mail size={13} /> Send us a message</Link>
                            <span><MapPin size={13} /> {CONTACT.location}</span>
                        </div>

                        <div className="site-footer__apps">
                            <span className="site-footer__eyebrow">Get the app</span>
                            <StoreBadge href={androidHref} download small="Download the" name="Android app" icon={<AndroidMark />} />
                            <StoreBadge href={appLinks.googlePlay} small="Get it on" name="Google Play" icon={<GooglePlayMark />} />
                            <StoreBadge href={appLinks.appStore} small="Download on the" name="App Store" icon={<AppleMark />} />
                        </div>
                    </div>
                </div>

                <div className="site-footer__bottom">
                    <p>© {new Date().getFullYear()} Gettimelam Matrimony. All rights reserved.</p>
                    <nav aria-label="Legal links">
                        <Link to="/about">About</Link>
                        <Link to="/success-stories">Success Stories</Link>
                        <Link to="/plans">Plans</Link>
                        <Link to="/faq">FAQ</Link>
                        <Link to="/blog">Blog</Link>
                        <Link to="/contact">Contact</Link>
                        <Link to="/privacy-policy">Privacy</Link>
                        <Link to="/terms-conditions">Terms</Link>
                    </nav>
                    <p>Made with <Heart size={12} fill="currentColor" /> for Indian families</p>
                </div>
            </div>

            <SupportChatWidget open={showSupportChat} onClose={() => setShowSupportChat(false)} />
            {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
        </footer>
    );
};

export default Footer;
